import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Scale, BookOpen, ChevronRight, Linkedin, Image } from "lucide-react";
import { HERO_IMG, PRACTICE_AREAS, TESTIMONIALS, MAJOR_CASES, ROTIMI_PORTRAIT, ROTIMI_LINKEDIN } from "../data/firmContent";
import api from "../lib/api";
import useSEO from "../lib/useSEO";

export default function Home() {
    useSEO({ path: "/" });
    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section className="relative bg-[#0F172A] text-[#faf9f6]" data-testid="home-hero">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="Justice scales" className="w-full h-full object-cover opacity-40" />
                    <div className="absolute inset-0 rj-overlay" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-40">
                    <div className="max-w-3xl rj-fade-up">
                        <div className="font-mono-cap text-xs text-[#c5a059] mb-6">Est. 1997 · Lagos · Abuja</div>
                        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl leading-[1.05] text-[#faf9f6]">
                            Counsel for the matters that <em className="text-[#c5a059] not-italic">define the record.</em>
                        </h1>
                        <p className="mt-8 text-[17px] lg:text-lg leading-relaxed text-[#faf9f6]/90 max-w-2xl">
                            Rotimi Jacobs &amp; Co., Legal Practitioners has spent more than a quarter-century at the
                            frontier of Nigerian law — prosecuting landmark cases, advising governments and standing
                            for clients when the stakes are highest.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-book-consultation-button"
                                className="inline-flex items-center gap-2 bg-[#c5a059] text-[#0F172A] px-7 py-3.5 rounded-sm font-medium hover:bg-[#faf9f6] transition-colors"
                            >
                                Book a Consultation <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/practice-areas"
                                data-testid="hero-practice-areas-button"
                                className="inline-flex items-center gap-2 border border-[#faf9f6]/30 text-[#faf9f6] px-7 py-3.5 rounded-sm font-medium hover:bg-[#faf9f6]/10 transition-colors"
                            >
                                Our Practice <ChevronRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* TICKER OF MAJOR CASES */}
            <section className="bg-[#0F172A] border-t border-[#c5a059]/30 overflow-hidden" data-testid="home-cases-ticker">
                <div className="relative">
                    <div className="flex whitespace-nowrap rj-marquee py-4">
                        {[...MAJOR_CASES, ...MAJOR_CASES].map((c, i) => (
                            <span key={i} className="font-mono-cap text-xs text-[#c5a059]/80 mx-8">
                                ◆ {c}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* INTRO + FIRM PILLARS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32" data-testid="home-intro">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="font-mono-cap text-xs text-[#c5a059] mb-4">The Firm</div>
                        <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight text-slate-900">
                            Innovative, competent, cost-effective and well-timed solutions.
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:pl-8">
                        <p className="text-slate-800 leading-relaxed text-[17px] lg:text-lg">
                            Founded in 1997, our chamber renders legal services to governments at various levels and
                            their agencies, public and private companies, Nigerian and foreign investors and financial
                            institutions. We are a leading light in asset tracing, white-collar crime litigation, real
                            estate, industrial relations and complex commercial transactions.
                        </p>
                        <div className="mt-10 grid grid-cols-3 gap-8">
                            <div>
                                <div className="font-serif-display text-4xl text-[#c5a059]">28+</div>
                                <div className="font-mono-cap text-[10px] text-slate-500 mt-2">Years of Practice</div>
                            </div>
                            <div>
                                <div className="font-serif-display text-4xl text-[#c5a059]">High Court → Supreme Court</div>
                                <div className="font-mono-cap text-[10px] text-slate-500 mt-2">Trial &amp; Appellate</div>
                            </div>
                            <div>
                                <div className="font-serif-display text-4xl text-[#c5a059]">SAN · CON</div>
                                <div className="font-mono-cap text-[10px] text-slate-500 mt-2">Principal Honours</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PRACTICE AREAS GRID */}
            <section className="bg-[#f3f1ea] border-y border-slate-900/10" data-testid="home-practice-areas">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <div className="flex items-end justify-between gap-8 mb-12">
                        <div>
                            <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Practice Areas</div>
                            <h2 className="font-serif-display text-3xl md:text-5xl text-slate-900">
                                Where our chamber leads.
                            </h2>
                        </div>
                        <Link
                            to="/practice-areas"
                            className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#c5a059]"
                            data-testid="home-all-practice-link"
                        >
                            View all <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-900/10">
                        {PRACTICE_AREAS.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/practice-areas/${p.slug}`}
                                className="group bg-[#faf9f6] p-8 lg:p-10 hover:bg-[#0F172A] transition-colors duration-300"
                                data-testid={`practice-area-card-${p.slug}`}
                            >
                                <div className="font-mono-cap text-xs text-slate-500 group-hover:text-[#c5a059] transition-colors">{p.kicker}</div>
                                <h3 className="font-serif-display text-2xl mt-6 text-slate-900 group-hover:text-[#c5a059] transition-colors">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-[15px] text-slate-800 group-hover:text-[#faf9f6] leading-relaxed transition-colors">{p.short}</p>
                                <div className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-slate-900 group-hover:text-[#c5a059] transition-colors">
                                    Explore <ArrowUpRight className="w-4 h-4" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* SPLIT - FOUNDER */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32" data-testid="home-founder">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-6 order-2 md:order-1">
                        <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Principal Partner</div>
                        <h2 className="font-serif-display text-3xl md:text-5xl text-slate-900 leading-tight">
                            Rotimi Jacobs, <em className="not-italic text-[#c5a059]">SAN, CON.</em>
                        </h2>
                        <p className="mt-6 text-[17px] text-slate-700 leading-relaxed">
                            Called to the Nigerian Bar in 1989, Rotimi Jacobs rose to Head of Gani Fawehinmi Chambers and served
                            on the Editorial Board of the Nigerian Weekly Law Report before founding Rotimi Jacobs & Co in 1997.
                            His practice spans financial fraud, regulatory compliance, dispute resolution, litigation, real
                            estate, corporate and general commercial law.
                        </p>
                        <p className="mt-4 text-[17px] text-slate-700 leading-relaxed">
                            He has acted for the Federal Government of Nigeria and many large companies, representing EFCC,
                            FIRS, CBN, the Federal Ministry of Justice and other agencies. He served as Special Assistant to the
                            Attorney General and Minister of Justice from 1999 to 2000 and was honoured as SAN in 2012 and CON in 2023.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/about"
                                className="inline-flex items-center gap-2 text-sm font-medium text-slate-900 border-b border-[#c5a059] pb-1 hover:text-[#c5a059]"
                                data-testid="home-founder-more-link"
                            >
                                More on the firm <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <a
                                href={ROTIMI_LINKEDIN}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="home-founder-linkedin"
                                className="inline-flex items-center gap-2 text-sm font-medium text-[#0F172A] bg-[#c5a059] px-4 py-2 rounded-sm hover:bg-[#0F172A] hover:text-[#faf9f6] transition-colors"
                            >
                                <Linkedin className="w-4 h-4" /> LinkedIn
                            </a>
                        </div>
                    </div>
                    <div className="md:col-span-6 order-1 md:order-2">
                        <div className="relative group overflow-hidden rounded-sm">
                            <img src={ROTIMI_PORTRAIT} alt="Rotimi Jacobs, SAN, CON — Principal Partner" className="w-full h-[520px] object-cover object-top rounded-sm transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/60 transition duration-500 flex items-end p-8">
                                <div className="opacity-0 group-hover:opacity-100 transition duration-500 text-[#faf9f6]">
                                    <p className="text-sm uppercase tracking-[0.24em] text-[#c5a059] mb-3">Principal Partner</p>
                                    <p className="text-base leading-relaxed max-w-md">
                                        Called to the Nigerian Bar in 1989; headed Gani Fawehinmi Chambers and founded Rotimi Jacobs & Co in 1997.
                                        His work spans financial fraud, regulatory compliance, dispute resolution, litigation, real estate, corporate and commercial law.
                                    </p>
                                </div>
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-[#c5a059] p-6 hidden md:block transition-transform duration-500 group-hover:-translate-y-2">
                                <Scale className="w-8 h-8 text-[#0F172A]" />
                                <div className="font-serif-display text-xl text-[#0F172A] mt-2">SAN · CON</div>
                                <div className="font-mono-cap text-[10px] text-[#0F172A]/70">Principal Honours</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="bg-[#0F172A] text-[#faf9f6] py-24" data-testid="home-testimonials">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Client Voices</div>
                    <h2 className="font-serif-display text-3xl md:text-5xl text-[#faf9f6] mb-16 max-w-2xl">
                        Trusted where the record matters.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#faf9f6]/10">
                        {TESTIMONIALS.map((t, i) => (
                            <blockquote
                                key={i}
                                className="bg-[#0F172A] p-8 lg:p-10"
                                data-testid={`testimonial-${i}`}
                            >
                                <div className="font-serif-display text-3xl text-[#c5a059] leading-none">“</div>
                                <p className="mt-4 text-[17px] leading-relaxed text-[#faf9f6]">{t.quote}</p>
                                <footer className="mt-6 font-mono-cap text-[11px] text-[#c5a059]">
                                    — {t.attribution}
                                </footer>
                            </blockquote>
                        ))}
                    </div>
                </div>
            </section>

            {/* GALLERY PREVIEW */}
            <section className="bg-[#f3f1ea]" data-testid="home-gallery-preview">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-12">
                        <div>
                            <div className="font-mono-cap text-xs text-[#c5a059] mb-3 flex items-center gap-2">
                                <Image className="w-3.5 h-3.5" /> Gallery
                            </div>
                            <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900 leading-tight">
                                Select images from our firm gallery.
                            </h2>
                        </div>
                        <Link
                            to="/gallery"
                            className="inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#c5a059]"
                            data-testid="home-gallery-button"
                        >
                            View full gallery <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <GalleryPreview />
                </div>
            </section>

            {/* PUBLICATIONS CTA */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="home-publications-cta">
                <div className="border border-slate-900/10 rounded-sm p-10 md:p-16 bg-[#faf9f6] flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                    <div className="max-w-xl">
                        <div className="font-mono-cap text-xs text-[#c5a059] mb-3 flex items-center gap-2">
                            <BookOpen className="w-3.5 h-3.5" /> Publications
                        </div>
                        <h3 className="font-serif-display text-3xl md:text-4xl text-slate-900">
                            Authoritative treatises by the Principal Partner.
                        </h3>
                        <p className="mt-4 text-slate-700">
                            <em>Rotimi Jacobs On Criminal Procedure</em> and <em>Rotimi Jacobs On Evidence</em> — now
                            available in hardcover and digital formats.
                        </p>
                    </div>
                    <Link
                        to="/publications"
                        data-testid="home-shop-publications-button"
                        className="inline-flex items-center gap-2 bg-[#0F172A] text-[#faf9f6] px-7 py-3.5 rounded-sm font-medium hover:bg-[#c5a059] hover:text-[#0F172A] transition-colors"
                    >
                        Browse the bookstore <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}

function GalleryPreview() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/gallery")
            .then(({ data }) => setImages(data.slice(0, 3)))
            .catch(() => setImages([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="font-mono-cap text-xs text-slate-500">Loading gallery…</div>;
    if (images.length === 0) return <div className="text-slate-600">No gallery images available yet.</div>;

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {images.map((image) => (
                <div key={image.id} className="overflow-hidden rounded-sm border border-slate-900/10 bg-white shadow-sm">
                    <div className="aspect-[4/3] bg-slate-100">
                        <img src={image.public_url} alt={image.original_filename || "Gallery image"} className="w-full h-full object-cover" />
                    </div>
                </div>
            ))}
        </div>
    );
}
